import { useEffect, useState } from 'react'
import { admin, type AdminClub, type AdminClubStaff, type AdminUserRow } from '../../api/admin'
import { ApiError } from '../../api/client'

const USER_TYPE_OPTIONS = [
  { value: 'jugador', label: 'Jugador' },
  { value: 'tester', label: 'Tester' },
  { value: 'manager', label: 'Manager del club' },
]

// Un solo rol de club (mg, 2026-09-17): club_admin y tecnico se retiraron.
type UserType = 'jugador' | 'tester' | 'manager'

/**
 * Modal para crear usuarios. Campos: email (required), password (tester/manager, min 6),
 * display_name (optional), Tipo (select), Club (conditional, required for some types).
 * Envía al backend POST /admin/users con initial_role y/o club_role según tipo.
 */
export function CreateUserModal({
  onClose,
  onUserCreated,
  initialEmail = '',
  initialClubId,
  lockClub = false,
}: {
  onClose: () => void
  onUserCreated: (user: AdminUserRow) => void
  /** Prellena el email: se abre desde la ficha del club con el mail que el
   *  operador ya tipeó en el buscador y no existía (mg, 2026-09-17). */
  initialEmail?: string
  /** Club preseleccionado. Con `lockClub` no se puede cambiar: abrir el modal
   *  desde un club y terminar creando al jugador en otro sería una trampa. */
  initialClubId?: number
  lockClub?: boolean
}) {
  const [clubs, setClubs] = useState<AdminClub[] | null>(null)
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [userType, setUserType] = useState<UserType>('jugador')
  const [clubId, setClubId] = useState<string>(
    initialClubId != null ? String(initialClubId) : '',
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AdminUserRow | null>(null)

  useEffect(() => {
    admin
      .clubs()
      .then((r) => setClubs(r.clubs))
      .catch((e) => {
        console.error('Error loading clubs:', e)
        setClubs([])
      })
  }, [])

  const clubRequired = userType === 'manager'
  const clubHidden = userType === 'tester'
  const passwordRequired = userType !== 'jugador'

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const payload: {
        email: string
        password?: string
        display_name?: string
        initial_role?: 'tester'
        club_id?: number
        club_role?: AdminClubStaff['club_role']
      } = {
        email: email.trim(),
      }

      if (passwordRequired) payload.password = password.trim()

      if (displayName.trim()) {
        payload.display_name = displayName.trim()
      }

      if (userType === 'tester') {
        payload.initial_role = userType
      } else if (clubRequired) {
        const cid = Number(clubId)
        if (!Number.isFinite(cid) || cid <= 0) {
          setError('Debes seleccionar un club válido.')
          setBusy(false)
          return
        }
        payload.club_id = cid
        payload.club_role = 'manager'
      } else if (userType === 'jugador' && clubId) {
        payload.club_id = Number(clubId)
      }

      const user = await admin.createUser(payload)
      setResult(user)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  function handleClose() {
    if (result) {
      onUserCreated(result)
    }
    onClose()
  }

  const done = result !== null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-md backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="panel flex max-w-md flex-col gap-md p-xl shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-md border-b border-outline pb-md">
          <h2 className="m-0 text-base font-medium normal-case tracking-normal text-text-primary">
            {done ? 'Usuario creado' : 'Crear usuario'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-outline bg-transparent text-sm text-text-muted hover:border-primary hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        {!done ? (
          <form onSubmit={submit} className="flex flex-col gap-md">
            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Email
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@dominio.com"
                required
                maxLength={254}
                autoFocus
              />
            </label>

            {passwordRequired ? (
              <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
                Contraseña (mínimo 6 caracteres)
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••"
                  required
                  minLength={6}
                />
              </label>
            ) : (
              <p className="text-sm text-text-muted">
                El jugador recibirá un mail para elegir su propio PIN.
              </p>
            )}

            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Nombre para mostrar (opcional)
              <input
                type="text"
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Juan Pérez"
                maxLength={255}
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Tipo
              <select
                className="input"
                value={userType}
                onChange={(e) => {
                  setUserType(e.target.value as UserType)
                  if (clubRequired && !lockClub) setClubId('')
                }}
              >
                {USER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            {!clubHidden && (
              <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
                {clubRequired ? 'Club (requerido)' : 'Club (opcional)'}
                <select
                  className="input"
                  value={clubId}
                  onChange={(e) => setClubId(e.target.value)}
                  required={clubRequired}
                  disabled={lockClub}
                >
                  <option value="">— Sin club —</option>
                  {clubs?.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {error && <div className="banner-err">{error}</div>}
            <div className="flex justify-end gap-sm">
              <button type="button" className="btn" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  busy ||
                  !email.includes('@') ||
                  (passwordRequired && password.trim().length < 6) ||
                  (clubRequired && !clubId)
                }
              >
                {busy ? 'Creando…' : 'Crear usuario'}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-md">
            <div className="banner-ok">
              Usuario <strong>{result.email}</strong> creado exitosamente.
              {userType === 'jugador' && <p>El mail para elegir su PIN quedó encolado.</p>}
            </div>
            <div className="text-sm text-text-muted">
              {result.display_name && (
                <div>
                  <strong>Nombre:</strong> {result.display_name}
                </div>
              )}
              <div>
                <strong>Username:</strong> {result.username}
              </div>
              {result.roles.length > 0 && (
                <div>
                  <strong>Roles:</strong> {result.roles.join(', ')}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-sm">
              <button type="button" className="btn" onClick={handleClose}>
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
